import type { ComponentPropsWithRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { clsx } from '../clsx';
import { Icon } from './Icon';
import styles from './IconButton.module.css';

export interface IconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  icon: LucideIcon;
  /** Accessible name, e.g. "Benachrichtigungen". */
  label: string;
  /** `outline` is the 34 px bell of the topbar; `ghost` has no border (card menus, close crosses). */
  variant?: 'outline' | 'ghost' | 'ghostInverse' | undefined;
  /** 34 px (topbar) or 28 px (inside rows and headers). */
  size?: 34 | 28 | undefined;
  /** Unread counter badge at the top-right corner (hidden when 0 or undefined). */
  badge?: number | undefined;
  /** Marks the trigger as the active one (accent-soft fill), e.g. while its popover is open. */
  active?: boolean | undefined;
}

/** Square icon-only button (the topbar bell): 34×34, hairline border, badge with the unread count. */
export function IconButton({
  icon,
  label,
  variant = 'outline',
  size = 34,
  badge,
  active = false,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={clsx(
        'pressable',
        styles.button,
        styles[variant],
        size === 28 && styles.small,
        active && styles.active,
        className,
      )}
      {...rest}
    >
      <Icon icon={icon} size={size === 28 ? 16 : 18} />
      {badge !== undefined && badge > 0 ? (
        <span className={styles.badge} aria-label={`${badge} ungelesen`}>
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
}
